<script setup lang="ts">
/* eslint-env vue/setup-compiler-macros */
import Dialog from 'primevue/dialog';
import Button from 'primevue/button';

interface Props {
    visible: boolean
    title: string
    actionLabel?: string
    actionDisabled?: boolean
    width?: string
    height?: string
    actionWidth?: string
}

withDefaults(defineProps<Props>(), {
    actionLabel: 'Save',
    actionDisabled: false,
    width: '930px',
    height: '',
    actionWidth: '156px',
});

const emit = defineEmits<{
    'update:visible': [value: boolean]
    cancel: []
    action: []
}>();

function close() {
    emit('update:visible', false);
    emit('cancel');
}
</script>

<template>
    <Dialog
        :visible="visible"
        modal
        :blockScroll="true"
        :closable="false"
        :draggable="false"
        :style="{ width, height: height || undefined }"
        :pt="{
            root: { class: 'app-modal' },
            header: { style: { display: 'none' } },
            content: { class: 'app-modal__content' },
            footer: { style: { display: 'none' } },
            mask: { class: 'app-modal__mask' },
        }"
        @update:visible="emit('update:visible', $event)"
    >
        <div class="app-modal__header">
            <span class="app-modal__title">{{ title }}</span>
            <button
                type="button"
                class="app-modal__close"
                aria-label="Close"
                @click="close"
            >
                <i class="pi pi-times" aria-hidden="true" />
            </button>
        </div>

        <span class="app-modal__hr" aria-hidden="true" />

        <div class="app-modal__body">
            <slot />
        </div>

        <span class="app-modal__hr" aria-hidden="true" />

        <div class="app-modal__footer">
            <slot name="footer">
                <Button
                    type="button"
                    label="Cancel"
                    class="app-modal__cancel-btn"
                    @click="close"
                />
                <Button
                    type="button"
                    :label="actionLabel"
                    :disabled="actionDisabled"
                    class="app-modal__action-btn"
                    :style="{ width: actionWidth }"
                    @click="emit('action')"
                />
            </slot>
        </div>
    </Dialog>
</template>

<style lang="scss" scoped>

.app-modal__header {
    display: flex;
    align-items: center;
    justify-content: space-between;
}

.app-modal__title {
    font-family: $font-family-archivo;
    font-weight: 500;
    font-size: 20px;
    line-height: 22px;
    color: $black-color;
}

.app-modal__close {
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 0;
    border: none;
    background: transparent;
    cursor: pointer;
    color: $main-orange;

    .pi {
        font-size: 16px;
        font-weight: 900;
    }
}

.app-modal__hr {
    display: block;
    width: 100%;
    height: 2px;
    margin: 20px 0;
    background: $hr-gray-color;
}

.app-modal__body {
    display: flex;
    flex-direction: column;
    gap: 25px;
    flex: 1;
    min-height: 0;
}

.app-modal__footer {
    display: flex;
    align-items: center;
    justify-content: flex-end;
    gap: 10px;
    padding-top: 10px;
}

.app-modal__cancel-btn {
    width: 91px !important;
    height: 46px !important;
    border-radius: 10px !important;
    border: 1px solid $border-gray-color !important;
    background: $white-color !important;
    font-family: $font-family-archivo !important;
    font-weight: 400 !important;
    font-size: 16px !important;
    line-height: 17px !important;
    color: rgba($black-color, 0.7) !important;
    box-shadow: none !important;
    padding: 0 !important;
    justify-content: center;
}

.app-modal__action-btn {
    height: 46px !important;
    border-radius: 10px !important;
    border: 1px solid $main-orange !important;
    background: $main-orange !important;
    font-family: $font-family-archivo !important;
    font-weight: 600 !important;
    font-size: 16px !important;
    line-height: 17px !important;
    color: $white-color !important;
    box-shadow: none !important;
    padding: 0 !important;
    justify-content: center;

    &:hover:not(:disabled) {
        background: $main-orange-hover !important;
        border-color: $main-orange-hover !important;
    }

    &:disabled {
        opacity: 0.5;
        cursor: not-allowed;
    }
}
</style>

<style lang="scss">
.p-dialog.app-modal {
    --p-dialog-border-radius: 5px;
    --p-dialog-shadow: 0 0 30px #{rgba($black-color, 0.1)};
    --p-dialog-border-color: #{$paginator-border-color};
    --p-dialog-content-padding: 30px 40px 30px;
    border: 1px solid $paginator-border-color;
    display: flex;
    flex-direction: column;
    max-height: 90vh;

    .app-modal__content {
        display: flex !important;
        flex-direction: column !important;
        padding: 30px 40px 30px !important;
        gap: 0;
        flex: 1;
        min-height: 0;
        overflow: hidden !important;
    }
}

html:has(.p-dialog-mask) {
    scrollbar-gutter: auto;
}
</style>
